import React, { useState, useEffect } from 'react';
import { Download, Smartphone, Copy, Check, Share2, X, Apple } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';

const INSTALL_URL = 'https://studio.saketh.ai';

const detectPlatform = () => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

/**
 * Install App button + fallback modal.
 * - If browser supports PWA install: clicking triggers native prompt.
 * - Otherwise: opens a modal with the URL, copy-link, QR code, and OS-specific instructions.
 *
 * Props:
 *  - variant: 'sidebar' | 'landing' | 'icon'  (style preset)
 */
export default function InstallApp({ variant = 'sidebar' }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const platform = detectPlatform();

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const installedHandler = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      toast.success('Saketh Studio installed!');
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  // Hide entirely if running as an installed PWA
  if (installed) return null;

  const handleClick = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          toast.success('Installing Saketh Studio...');
        }
        setDeferredPrompt(null);
        return;
      } catch (err) {
        // fall through to modal
      }
    }
    setShowModal(true);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(INSTALL_URL);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy. Long-press the link to copy.');
    }
  };

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=12&bgcolor=09090b&color=fafafa&data=${encodeURIComponent(INSTALL_URL)}`;

  // --- Trigger button (three style variants) ---
  let trigger;
  if (variant === 'landing') {
    trigger = (
      <Button
        onClick={handleClick}
        data-testid="install-app-btn-landing"
        className="h-12 px-6 rounded-xl font-semibold bg-zinc-100 text-zinc-950 hover:bg-white transition-all duration-200 hover:-translate-y-0.5 shadow-lg"
      >
        <Download className="w-5 h-5 mr-2" />
        Install App
      </Button>
    );
  } else if (variant === 'icon') {
    trigger = (
      <button
        onClick={handleClick}
        data-testid="install-app-btn-icon"
        aria-label="Install App"
        className="p-2 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 transition-colors"
      >
        <Download className="w-5 h-5" />
      </button>
    );
  } else {
    // sidebar (default)
    trigger = (
      <button
        onClick={handleClick}
        data-testid="install-app-btn-sidebar"
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-300 hover:text-zinc-100 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 hover:from-emerald-500/20 hover:to-teal-500/20 border border-emerald-500/20 hover:border-emerald-500/40 transition-all duration-200"
      >
        <Download className="w-5 h-5 text-emerald-400" />
        <div className="flex-1 text-left">
          <p className="text-sm font-medium leading-tight">Install App</p>
          <p className="text-[11px] text-zinc-500 leading-tight">Use offline & fullscreen</p>
        </div>
      </button>
    );
  }

  return (
    <>
      {trigger}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent
          className="max-w-md bg-zinc-950 border-zinc-800"
          data-testid="install-app-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-emerald-400" />
              Install Saketh Studio
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            {/* QR + URL */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-zinc-900/60 border border-zinc-800">
              <img
                src={qrUrl}
                alt="QR code for studio.saketh.ai"
                width="120"
                height="120"
                className="rounded-lg border border-zinc-800 flex-shrink-0"
                data-testid="install-qr-code"
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-zinc-500 mb-1">Scan or open on your phone</p>
                <p className="text-sm font-mono text-zinc-200 break-all">{INSTALL_URL}</p>
                <button
                  onClick={copyLink}
                  data-testid="install-copy-link-btn"
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-400 hover:text-emerald-300"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" /> Copy link
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Platform-specific instructions */}
            <div className="space-y-3">
              {/* iOS */}
              <div
                className={`p-3 rounded-xl border ${
                  platform === 'ios'
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-zinc-900/40 border-zinc-800'
                }`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Apple className="w-4 h-4 text-zinc-300" />
                  <p className="text-sm font-semibold text-zinc-100">iPhone / iPad</p>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Open in <span className="text-zinc-200 font-medium">Safari</span> → tap{' '}
                  <Share2 className="w-3 h-3 inline-block mx-0.5" /> Share → scroll &amp; tap{' '}
                  <span className="text-zinc-200 font-medium">Add to Home Screen</span>.
                </p>
              </div>

              {/* Android */}
              <div
                className={`p-3 rounded-xl border ${
                  platform === 'android'
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-zinc-900/40 border-zinc-800'
                }`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Smartphone className="w-4 h-4 text-zinc-300" />
                  <p className="text-sm font-semibold text-zinc-100">Android</p>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Open in <span className="text-zinc-200 font-medium">Chrome</span> → tap the
                  menu (⋮) → tap <span className="text-zinc-200 font-medium">Install app</span> or{' '}
                  <span className="text-zinc-200 font-medium">Add to Home screen</span>.
                </p>
              </div>

              {/* Desktop */}
              <div
                className={`p-3 rounded-xl border ${
                  platform === 'desktop'
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-zinc-900/40 border-zinc-800'
                }`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Download className="w-4 h-4 text-zinc-300" />
                  <p className="text-sm font-semibold text-zinc-100">Desktop (Chrome / Edge)</p>
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Look for the <span className="text-zinc-200 font-medium">install icon</span> (⊕)
                  in the address bar, or open menu → <span className="text-zinc-200 font-medium">Install Saketh Studio</span>.
                </p>
              </div>
            </div>

            <Button
              variant="ghost"
              onClick={() => setShowModal(false)}
              data-testid="install-modal-close-btn"
              className="w-full h-10 text-zinc-400 hover:text-zinc-200"
            >
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
